import { useCallback, useEffect, useState } from 'react';

import { HighlightOff } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Link,
  List,
  ListItem,
  Typography,
} from '@mui/material';

import { fetchOrganizationById, patchOrganization } from 'api';
import { AlertModal } from 'components';
import { Organization, AssociatedEntity } from 'components/Labels';
import { useSnackContext } from 'hooks/useSnackContext';
import { OrganizationVerboseProps } from 'types';

export type AssociatedEditFormProps = {
  organization: OrganizationVerboseProps;
  setOrganization: React.Dispatch<
    React.SetStateAction<OrganizationVerboseProps>
  >;
};

export const AssociatedEdit = (props: AssociatedEditFormProps) => {
  const { organization, setOrganization } = props;
  const [fullParent, setFullParent] = useState<OrganizationVerboseProps | null>(
    null,
  );
  const { setSnack } = useSnackContext();
  const { parent, parent_id, id } = organization;
  const [convertToOrgAlert, setConvertToOrgAlert] = useState(false);
  const [convertingToOrg, setConvertingToOrg] = useState(false);
  const handleConvertToOrganization = useCallback(
    async (e: React.FormEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        setConvertingToOrg(true);
        const { data } = await patchOrganization(id, { parent: null });
        setOrganization(data);
      } catch (e) {
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: 'Failed To Convert Organization',
          error: e as Error,
        }));
      }
      setConvertingToOrg(false);
    },
    [id, setOrganization, setSnack],
  );

  useEffect(() => {
    const getParent = async () => {
      try {
        const { data } = await fetchOrganizationById(parent_id!);
        setFullParent(data);
      } catch (e) {
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: 'Failed To Fetch Organization',
          error: e as Error,
        }));
      }
    };
    getParent();
  }, [parent_id, setSnack]);

  return (
    <>
      <Grid container>
        <Grid item xs={5.5}>
          <Typography variant='body2' sx={{ mt: 1 }}>
            This Employer is currently set as an <AssociatedEntity /> and is
            already associated with the <Organization />{' '}
            <Link variant='body2' href={`${parent_id}`}>
              {parent?.name ? parent.name : ''}
            </Link>
            .
          </Typography>
          <Typography variant='body2' sx={{ mt: 1 }}>
            Please visit the page for{' '}
            <Link href={`${parent_id}`}>{parent?.name ? parent.name : ''}</Link>
            . to add Employers as Associated Entities, or click{' '}
            <Link variant='body2' onClick={() => setConvertingToOrg(true)}>
              here
            </Link>{' '}
            to remove this employer’s association and make it a standalone
            organization.
          </Typography>
        </Grid>
        <Grid item xs={1} />
        <Grid item xs={5.5}>
          <Button
            sx={{
              color: '#5e5e5e',
              textAlign: 'left',
              textDecoration: 'underline',
            }}
            onClick={() => setConvertToOrgAlert(true)}
            startIcon={<HighlightOff fontSize='large' color='error' />}
          >
            Remove association and convert to an Organization
          </Button>
        </Grid>
        <Grid item xs={12}>
          {!fullParent ? (
            <LinearProgress sx={{ width: '100%' }} />
          ) : (
            <Box sx={{ outline: '1px solid #DCDEE0', mt: 2 }}>
              <List>
                <ListItem>
                  <Box sx={{ display: 'flex', flexFlow: 'column' }}>
                    <Typography variant='overline'>
                      {' '}
                      <Organization />
                    </Typography>
                    <Link href={`${fullParent.id}`} variant='body1'>
                      {fullParent.name}
                    </Link>
                  </Box>
                </ListItem>
              </List>
              <List>
                <ListItem>
                  <Typography
                    sx={{ fontWeight: 'bold' }}
                    variant='overline'
                    color='success.dark'
                  >
                    ASSOCIATED ENTITIES{' '}
                  </Typography>
                </ListItem>
                {fullParent.children.map((child) => {
                  return (
                    <ListItem key={child.id}>
                      {child.id === id ? (
                        <>
                          <Typography variant='body1'>{child.name}</Typography>
                          <Chip sx={{ ml: 1 }} label='THIS EMPLOYER' />
                        </>
                      ) : (
                        <Link variant='body1' href={`${child.id}`}>
                          {child.name}
                        </Link>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </Grid>
      </Grid>
      <AlertModal
        maxWidth='xs'
        open={convertToOrgAlert}
        heading='Convert to Organization'
        subHeading='Users associated with this entities organization will no longer be able to view its candidates'
      >
        <Button
          sx={{ mr: 1 }}
          variant='outlined'
          onClick={() => setConvertToOrgAlert(false)}
        >
          CANCEL
        </Button>
        <LoadingButton
          sx={{ ml: 1 }}
          variant='contained'
          loading={convertingToOrg}
          onClick={handleConvertToOrganization}
        >
          CONVERT
        </LoadingButton>
      </AlertModal>
    </>
  );
};
